/**
 * Tasks route layout. Hosts the page header + sub-tab strip and an
 * Outlet for the nested sub-views: Today (default), Board, Table,
 * Stats. Each sub-view lives in its own file (TanStack Router file
 * routes with dot-prefix nesting).
 *
 * PR #41 reshuffled the sub-views so Today is the landing tab — the
 * morning workflow opens the Tasks section to "what wants attention
 * now" rather than the full Kanban. Board moved to `/tasks/board` and
 * the index route now renders the Today view (two stacked tables for
 * Frogs and Due today).
 *
 * Reference: spec section 15.5.
 */

import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/tasks')({
  component: TasksLayout,
});

const TABS = [
  { to: '/tasks', label: 'TODAY' },
  { to: '/tasks/board', label: 'BOARD' },
  { to: '/tasks/table', label: 'TABLE' },
  { to: '/tasks/stats', label: 'STATS' },
] as const;

function TabStrip() {
  const { pathname } = useLocation();
  return (
    <nav className="flex gap-mx-4 border-b border-mx-grey-800" aria-label="Tasks views">
      {TABS.map((tab) => {
        const active = tab.to === '/tasks' ? pathname === '/tasks' : pathname.startsWith(tab.to);
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={cn(
              'mono-label relative pb-mx-2 pt-mx-1 text-mx-grey-mid hover:text-mx-white',
              active && 'text-mx-white',
            )}
          >
            {tab.label}
            {active && (
              <span
                aria-hidden
                className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-mx-orange"
              />
            )}
          </Link>
        );
      })}
    </nav>
  );
}

function TasksLayout() {
  return (
    <>
      <PageHeader eyebrowNumber="01" eyebrowText="Tasks" title="Tasks" tabs={<TabStrip />} />
      <Outlet />
    </>
  );
}
